import React, {
	createContext,
	useContext,
	useCallback,
	useMemo,
	ReactNode,
	useState,
	useEffect,
	useRef,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSite } from '../../hooks/useSite';
import { logError } from '../../utils/logger';
import { postMessageToParent } from '../../message-bus/postMessageToParent';
import {
	saveRovoExtensionAdminSettings,
	RovoExtAdminSettings,
} from '../gateway-client/gatewayClient';
import {
	clearCachedRemoteAdminSettings,
	getAdminSettingsQueryKey,
	getAdminSettingsQueryOptions,
	setCachedRemoteAdminSettings,
} from './adminSettingsQuery';

export { SettingsQueryKeys } from './adminSettingsQuery';
import {
	SettingsContextValue,
	RemoteAdminSettingsError,
	RemoteAdminSettingsErrorType,
	UpdateRemoteSettingParams,
	LocalStoredSettings,
	LocalStoredSettingsKey,
	UpdateLocalStoredSettingParams,
	ChromeStoredSettings,
	ChromeStoredSettingsKey,
	UpdateChromeStoredSettingParams,
} from './types';
import { LocalStorageKey } from '../localStorage';
import { useStateWithSync } from '../../hooks/useStateWithSync';
import {
	DEFAULT_CHROME_STORED_SETTINGS,
	DEFAULT_LOCAL_STORED_SETTINGS,
	DEFAULT_REMOTE_ADMIN_SETTINGS,
	DEFAULT_USER_SETTINGS,
} from './constants';
import type { DefaultedRemoteAdminSettings } from './types';
import { isValidHttpUrl } from '../../utils/isValidHttpUrl';

/**
 * The three admin NTP enforcement modes. The `controlledUserNtpEnabled` /
 * `defaultNtpUrl` tuple is the source of truth — see the
 * `effectiveRemoteAdminSettings` memo for the exact mapping.
 */
type NtpEnforcementMode = 'noEnforcement' | 'enforceRovo' | 'enforceCustomUrl';

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export interface SettingsProviderProps {
	children: ReactNode;
}

export const SettingsProvider = ({ children }: SettingsProviderProps): React.JSX.Element => {
	const [chromeStoredSettings, setChromeStoredSettings] = useState<ChromeStoredSettings>(
		DEFAULT_CHROME_STORED_SETTINGS,
	);

	useEffect(() => {
		const messageHandler = (event: MessageEvent): void => {
			switch (event.data.type) {
				case 'GET_OPTIONS_RESPONSE':
				case 'SET_OPTIONS_RESPONSE': {
					setChromeStoredSettings((prev) => ({
						...prev,
						...event.data.options,
					}));
					break;
				}
				default:
					break;
			}
		};

		window.addEventListener('message', messageHandler, false);
		postMessageToParent({ type: 'GET_OPTIONS' });

		return (): void => {
			window.removeEventListener('message', messageHandler);
		};
	}, []);

	const updateChromeStoredSetting = useCallback(
		<K extends ChromeStoredSettingsKey>(params: UpdateChromeStoredSettingParams<K>): void => {
			setChromeStoredSettings((prev) => ({
				...prev,
				[params.key]: params.value,
			}));

			postMessageToParent({
				type: 'SET_OPTIONS',
				updated: {
					[params.key]: params.value,
				},
				...(params.key === 'rovoCompanionEnabled' && params.value === false
					? { suppressCompanionDisabledPopup: true }
					: {}),
			});
		},
		[],
	);

	const [welcomeHeaderEnabled, setWelcomeHeaderEnabled] = useStateWithSync(
		LocalStorageKey.WELCOME_HEADER_ENABLED,
		true,
	);
	const [globalCreateEnabled, setGlobalCreateEnabled] = useStateWithSync(
		LocalStorageKey.GLOBAL_CREATE_ENABLED,
		true,
	);
	const [localDevBannerEnabled, setLocalDevBannerEnabled] = useStateWithSync(
		LocalStorageKey.LOCAL_DEV_BANNER_ENABLED,
		true,
	);
	const [calendarEnabled, setCalendarEnabled] = useStateWithSync(
		LocalStorageKey.CALENDAR_ENABLED,
		false,
	);
	const [recentWorkEnabled, setRecentWorkEnabled] = useStateWithSync(
		LocalStorageKey.RECENT_WORK_ENABLED,
		true,
	);
	const [bookmarksEnabled, setBookmarksEnabled] = useStateWithSync(
		LocalStorageKey.BOOKMARKS_ENABLED,
		true,
	);
	const [announcementsEnabled, setAnnouncementsEnabled] = useStateWithSync(
		LocalStorageKey.ANNOUNCEMENTS_ENABLED,
		true,
	);

	const localStoredSettings = useMemo<LocalStoredSettings>(
		() => ({
			[LocalStorageKey.WELCOME_HEADER_ENABLED]: welcomeHeaderEnabled,
			[LocalStorageKey.GLOBAL_CREATE_ENABLED]: globalCreateEnabled,
			[LocalStorageKey.LOCAL_DEV_BANNER_ENABLED]: localDevBannerEnabled,
			[LocalStorageKey.CALENDAR_ENABLED]: calendarEnabled,
			[LocalStorageKey.RECENT_WORK_ENABLED]: recentWorkEnabled,
			[LocalStorageKey.BOOKMARKS_ENABLED]: bookmarksEnabled,
			[LocalStorageKey.ANNOUNCEMENTS_ENABLED]: announcementsEnabled,
		}),
		[
			welcomeHeaderEnabled,
			globalCreateEnabled,
			localDevBannerEnabled,
			calendarEnabled,
			recentWorkEnabled,
			bookmarksEnabled,
			announcementsEnabled,
		],
	);

	const updateLocalStoredSetting = useCallback(
		<K extends LocalStoredSettingsKey>(params: UpdateLocalStoredSettingParams<K>): void => {
			switch (params.key) {
				case LocalStorageKey.WELCOME_HEADER_ENABLED:
					setWelcomeHeaderEnabled(params.value);
					return;
				case LocalStorageKey.GLOBAL_CREATE_ENABLED:
					setGlobalCreateEnabled(params.value);
					return;
				case LocalStorageKey.LOCAL_DEV_BANNER_ENABLED:
					setLocalDevBannerEnabled(params.value);
					return;
				case LocalStorageKey.CALENDAR_ENABLED:
					setCalendarEnabled(params.value);
					return;
				case LocalStorageKey.RECENT_WORK_ENABLED:
					setRecentWorkEnabled(params.value);
					return;
				case LocalStorageKey.BOOKMARKS_ENABLED:
					setBookmarksEnabled(params.value);
					return;
				case LocalStorageKey.ANNOUNCEMENTS_ENABLED:
					setAnnouncementsEnabled(params.value);
					return;
				default:
					return;
			}
		},
		[
			setWelcomeHeaderEnabled,
			setGlobalCreateEnabled,
			setLocalDevBannerEnabled,
			setCalendarEnabled,
			setRecentWorkEnabled,
			setBookmarksEnabled,
			setAnnouncementsEnabled,
		],
	);
	const siteContext = useSite({ allowUndefined: true });
	const siteId = siteContext ? siteContext.site.workspace.cloudId : undefined;
	const queryClient = useQueryClient();
	const [remoteAdminSettingsMutationError, setRemoteAdminSettingsMutationError] =
		useState<RemoteAdminSettingsError | null>(null);

	const {
		data: remoteAdminSettings = DEFAULT_REMOTE_ADMIN_SETTINGS,
		isLoading: isLoadingRemoteAdminSettings,
		error: queryErrorRaw,
	} = useQuery({
		...getAdminSettingsQueryOptions(siteId),
		throwOnError: false,
	});

	const previousErrorRef = useRef<Error | null>(null);

	// Handle error logging once when error state changes
	useEffect(() => {
		if (queryErrorRaw && queryErrorRaw !== previousErrorRef.current) {
			logError({ error: queryErrorRaw, impact: 'Failed to fetch remote admin settings' });
			previousErrorRef.current = queryErrorRaw;
		}
	}, [queryErrorRaw]);

	const remoteAdminSettingsQueryError = useMemo<RemoteAdminSettingsError | null>(() => {
		if (!queryErrorRaw) {
			return null;
		}
		return new RemoteAdminSettingsError({
			type: RemoteAdminSettingsErrorType.QUERY_ERROR,
			originalError: queryErrorRaw,
		});
	}, [queryErrorRaw]);

	const mutation = useMutation<
		RovoExtAdminSettings,
		Error,
		Partial<RovoExtAdminSettings>,
		{ previousSettings: RovoExtAdminSettings | undefined }
	>({
		mutationFn: async (data) => {
			if (!siteId) {
				throw new Error('siteId is required to update remote admin settings');
			}
			return saveRovoExtensionAdminSettings(siteId, data);
		},
		onMutate: async (newData) => {
			// Cancel any outgoing refetches to avoid overwriting optimistic update
			await queryClient.cancelQueries({
				queryKey: getAdminSettingsQueryKey(siteId),
			});

			// Snapshot the previous value.
			// NOTE: React Query cache may be empty on first load; keep a concrete fallback so we can always rollback.
			const previousSettingsFromCache = queryClient.getQueryData<RovoExtAdminSettings>(
				getAdminSettingsQueryKey(siteId),
			);
			const previousSettings = previousSettingsFromCache ?? DEFAULT_REMOTE_ADMIN_SETTINGS;

			// Optimistically update to the new value
			const optimisticSettings: RovoExtAdminSettings = {
				...previousSettings,
				...newData,
			};
			queryClient.setQueryData<RovoExtAdminSettings>(
				getAdminSettingsQueryKey(siteId),
				optimisticSettings,
			);

			// Return context with the previous value for rollback
			return { previousSettings };
		},
		onError: (originalError, newData, context) => {
			// Rollback to previous value on error
			queryClient.setQueryData<RovoExtAdminSettings>(
				getAdminSettingsQueryKey(siteId),
				context?.previousSettings ?? DEFAULT_REMOTE_ADMIN_SETTINGS,
			);

			const settingKey = Object.keys(newData)[0] as keyof RovoExtAdminSettings;
			const error = new RemoteAdminSettingsError({
				type: RemoteAdminSettingsErrorType.MUTATION_ERROR,
				originalError,
				context: {
					settingKey,
					attemptedValue: Object.values(newData)[0],
					previousValue: context?.previousSettings?.[settingKey],
				},
			});

			setRemoteAdminSettingsMutationError(error);
			logError({ error: originalError, impact: 'Failed to update remote admin settings' });
		},
		onSuccess: (data) => {
			// Update chrome.storage.local so other tabs immediately see the new settings
			if (siteId) {
				setCachedRemoteAdminSettings(siteId, data);
			}
		},
		onSettled: () => {
			// Refetch to ensure we have the latest data
			void queryClient.invalidateQueries({
				queryKey: getAdminSettingsQueryKey(siteId),
			});
		},
	});

	const updateRemoteAdminSettings = useCallback(
		async <K extends keyof RovoExtAdminSettings>(
			params: UpdateRemoteSettingParams<K>,
		): Promise<void> => {
			if (!siteId) {
				return;
			}

			// Clear previous mutation error when starting a new mutation
			setRemoteAdminSettingsMutationError(null);

			try {
				await mutation.mutateAsync({
					[params.key]: params.value,
				} as Partial<RovoExtAdminSettings>);
			} catch {
				// Error is handled by onError callback, but we need to catch it here
				// to prevent unhandled promise rejection
			}
		},
		[siteId, mutation],
	);

	const refreshRemoteAdminSettings = useCallback(async (): Promise<void> => {
		if (!siteId) {
			return;
		}
		// Step 1: Clear chrome.storage.local cache and wait for it to be processed
		await clearCachedRemoteAdminSettings(siteId);
		// Step 2: Invalidate React Query state, triggering a refetch
		await queryClient.invalidateQueries({
			queryKey: getAdminSettingsQueryKey(siteId),
		});
	}, [siteId, queryClient]);

	const effectiveRemoteAdminSettings: DefaultedRemoteAdminSettings = useMemo(() => {
		const merged = {
			...DEFAULT_REMOTE_ADMIN_SETTINGS,
			...remoteAdminSettings,
		};
		// The admin surface is a single toggle plus an opt-in URL field; together
		// they encode three enforcement modes:
		//
		//   `controlledUserNtpEnabled === true`         → mode 1 (enforce Rovo NTP
		//                                                 on all users)
		//   `controlledUserNtpEnabled === false` + no
		//   valid `defaultNtpUrl` (or undefined)        → mode 2 (no enforcement;
		//                                                 users default to Rovo
		//                                                 and may opt out)
		//   `controlledUserNtpEnabled === false` plus a
		//   valid `defaultNtpUrl`                       → mode 3 (enforce that URL
		//                                                 on all users)
		//
		// The admin-set redirect URL is therefore only meaningful in mode 3 — i.e.
		// when `controlledUserNtpEnabled === false`. Mask it everywhere else so
		// chrome.storage, the user-side hide-when-active gate, and the admin-side
		// defaulting in `DefaultNtpUrlField` all see the same coherent value
		// without each duplicating the gate.
		const isAdminDefaultNtpUrlInScope = merged.controlledUserNtpEnabled === false;
		return {
			...merged,
			defaultNtpUrl: isAdminDefaultNtpUrlInScope ? merged.defaultNtpUrl : '',
		};
	}, [remoteAdminSettings]);

	const syncAdminControlledFeaturesToChrome = useCallback(
		(settings: DefaultedRemoteAdminSettings): void => {
			postMessageToParent({
				type: 'SET_ADMIN_CONTROLLED_FEATURES',
				features: {
					definitionsAppEnabled: settings.controlledUserGdocsDefinitionsEnabled !== false,
					defaultNtpUrl: settings.defaultNtpUrl,
				},
			});
		},
		[],
	);

	useEffect(() => {
		if (isLoadingRemoteAdminSettings) {
			return;
		}
		syncAdminControlledFeaturesToChrome(effectiveRemoteAdminSettings);
	}, [
		effectiveRemoteAdminSettings,
		isLoadingRemoteAdminSettings,
		syncAdminControlledFeaturesToChrome,
	]);

	const previousNtpEnforcementModeRef = useRef<NtpEnforcementMode | undefined>(undefined);
	const previousControlledUserCompanionEnabledRef =
		useRef<RovoExtAdminSettings['controlledUserRovoCompanionEnabled']>(undefined);
	const previousControlledUserCompanyAnnouncementsRef =
		useRef<RovoExtAdminSettings['controlledUserCompanyAnnouncements']>(undefined);

	useEffect(() => {
		if (isLoadingRemoteAdminSettings) {
			return;
		}

		const currentNtp = effectiveRemoteAdminSettings.controlledUserNtpEnabled;
		const adminUrl = effectiveRemoteAdminSettings.defaultNtpUrl;

		// Tri-state mode resolution. `defaultNtpUrl` has already been masked to
		// `""` when the admin is not in mode 3, so a non-empty + http(s)-valid
		// value here is the unambiguous mode-3 signal.
		const currentMode: NtpEnforcementMode =
			currentNtp === true
				? 'enforceRovo'
				: currentNtp === false && adminUrl.length > 0 && isValidHttpUrl(adminUrl)
					? 'enforceCustomUrl'
					: 'noEnforcement';
		const previousMode = previousNtpEnforcementModeRef.current;

		if (currentMode !== previousMode) {
			if (currentMode === 'enforceRovo') {
				// Force the user's stored `ntpEnabled` to true so `setupNewTabPage`
				// renders the iframe regardless of any prior opt-out.
				postMessageToParent({
					type: 'SET_OPTIONS',
					updated: { ntpEnabled: true },
				});
			} else if (currentMode === 'enforceCustomUrl') {
				// Force `ntpEnabled = false` so the runtime falls into the redirect
				// branch and applies the admin's URL.
				postMessageToParent({
					type: 'SET_OPTIONS',
					updated: { ntpEnabled: false },
				});
			} else if (previousMode === 'enforceRovo' || previousMode === 'enforceCustomUrl') {
				// Admin lifted enforcement → reset the user's stored value to the
				// default. The user can then opt in/out from their own settings.
				postMessageToParent({
					type: 'SET_OPTIONS',
					updated: { ntpEnabled: DEFAULT_USER_SETTINGS.ntpEnabled },
				});
			}
		}

		previousNtpEnforcementModeRef.current = currentMode;
	}, [
		effectiveRemoteAdminSettings.controlledUserNtpEnabled,
		effectiveRemoteAdminSettings.defaultNtpUrl,
		isLoadingRemoteAdminSettings,
	]);

	useEffect(() => {
		if (isLoadingRemoteAdminSettings) {
			return;
		}

		const currentCompanion = effectiveRemoteAdminSettings.controlledUserRovoCompanionEnabled;
		const previousCompanion = previousControlledUserCompanionEnabledRef.current;

		// Admin disables Companion for all users (transition to false)
		if (currentCompanion === false && previousCompanion !== false) {
			postMessageToParent({
				type: 'SET_OPTIONS',
				updated: {
					rovoCompanionEnabled: false,
				},
			});
		}

		// Admin re-enables Companion for all users (false -> true/undefined)
		if (previousCompanion === false && currentCompanion !== false) {
			postMessageToParent({
				type: 'SET_OPTIONS',
				updated: {
					rovoCompanionEnabled: DEFAULT_USER_SETTINGS.rovoCompanionEnabled,
				},
			});
		}

		previousControlledUserCompanionEnabledRef.current = currentCompanion;
	}, [
		effectiveRemoteAdminSettings.controlledUserRovoCompanionEnabled,
		isLoadingRemoteAdminSettings,
	]);

	useEffect(() => {
		if (isLoadingRemoteAdminSettings) {
			return;
		}

		const currentAnnouncements = effectiveRemoteAdminSettings.controlledUserCompanyAnnouncements;
		const previousAnnouncements = previousControlledUserCompanyAnnouncementsRef.current;

		// Admin disables Company Announcements for all users (transition to false)
		if (currentAnnouncements === false && previousAnnouncements !== false) {
			setAnnouncementsEnabled(false);
		}

		// Admin re-enables Company Announcements for all users (false -> true/undefined)
		if (previousAnnouncements === false && currentAnnouncements !== false) {
			setAnnouncementsEnabled(DEFAULT_LOCAL_STORED_SETTINGS[LocalStorageKey.ANNOUNCEMENTS_ENABLED]);
		}

		previousControlledUserCompanyAnnouncementsRef.current = currentAnnouncements;
	}, [
		effectiveRemoteAdminSettings.controlledUserCompanyAnnouncements,
		isLoadingRemoteAdminSettings,
		setAnnouncementsEnabled,
	]);

	const contextValue = useMemo<SettingsContextValue>(
		() => ({
			remoteAdminSettings: effectiveRemoteAdminSettings,
			updateRemoteAdminSettings,
			refreshRemoteAdminSettings,
			isLoadingRemoteAdminSettings,
			remoteAdminSettingsQueryError,
			remoteAdminSettingsMutationError,
			localStoredSettings,
			updateLocalStoredSetting,
			chromeStoredSettings,
			updateChromeStoredSetting,
		}),
		[
			effectiveRemoteAdminSettings,
			updateRemoteAdminSettings,
			refreshRemoteAdminSettings,
			isLoadingRemoteAdminSettings,
			remoteAdminSettingsQueryError,
			remoteAdminSettingsMutationError,
			localStoredSettings,
			updateLocalStoredSetting,
			chromeStoredSettings,
			updateChromeStoredSetting,
		],
	);

	return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): SettingsContextValue => {
	const context = useContext(SettingsContext);

	if (context === undefined) {
		throw new Error('useSettings must be used within a SettingsProvider');
	}

	return context;
};
